import React, { useState, useMemo, useCallback } from 'react';
import { collection, query, orderBy, where, limit, startAfter, getDocs } from 'firebase/firestore';
import { useAppContext } from '../AppContext';
import { useTranslation } from 'react-i18next';
import CommentCard from './CommentCard';
import CommentForm from './CommentForm';

const CommentThread = ({ comment: initialComment, entryId, userProfiles, contentType, nexusId, onCommentDeleted, parentPath = null, nestingLevel = 0, topLevelParentId = null }) => {
  const { db, appId, LucideIcons } = useAppContext();
  const { t } = useTranslation();
  const [comment, setComment] = useState(initialComment);
  const [showReplies, setShowReplies] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [replyInitialContent, setReplyInitialContent] = useState('');

  const [replies, setReplies] = useState([]);
  const [lastReplyVisible, setLastReplyVisible] = useState(null);
  const [hasMoreReplies, setHasMoreReplies] = useState(true);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  const currentNestingLevel = nestingLevel;

  const authorProfile = userProfiles.find(p => p.id === comment.authorId);
  
  const currentPath = useMemo(() => {
    // parentPath is the path to the PARENT DOCUMENT.
    // For a top-level comment, parentPath is null.
    if (parentPath) {
      return `${parentPath}/replies/${comment.id}`;
    }
    
    // This is a top-level comment, construct its path from scratch.
    let basePath;
    if (contentType === 'moment') {
        basePath = `artifacts/${appId}/public/data/moments/${entryId}`;
    } else if (contentType === 'nexusPost' && nexusId) {
        basePath = `artifacts/${appId}/public/data/nexuses/${nexusId}/posts/${entryId}`;
    } else {
        basePath = `artifacts/${appId}/public/data/whispers/${entryId}`;
    }
    return `${basePath}/comments/${comment.id}`;
  }, [appId, entryId, contentType, nexusId, parentPath, comment.id]);

  const fetchReplies = useCallback(async () => {
    if (isLoadingReplies || !hasMoreReplies) return;
    setIsLoadingReplies(true);

    let repliesQuery = query(
      collection(db, `${currentPath}/replies`),
      orderBy("timestamp", "asc"),
      limit(5)
    );

    if (lastReplyVisible) {
        repliesQuery = query(repliesQuery, startAfter(lastReplyVisible));
    }

    try {
        const documentSnapshots = await getDocs(repliesQuery);
        const newReplies = documentSnapshots.docs.map(d => ({ id: d.id, ...d.data() }));
        
        setReplies(prev => [...prev, ...newReplies]);
        
        const lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
        setLastReplyVisible(lastVisible);

        if (documentSnapshots.docs.length < 5) {
            setHasMoreReplies(false);
        }
    } catch (error) {
        console.error("Error fetching replies:", error);
    } finally {
        setIsLoadingReplies(false);
    }
  }, [db, currentPath, isLoadingReplies, hasMoreReplies, lastReplyVisible]);

  const handleToggleReplies = () => {
      const newShowState = !showReplies;
      setShowReplies(newShowState);
      if (newShowState && replies.length === 0 && comment.replyCount > 0) {
          fetchReplies();
      }
  }

  const handleReplySuccess = () => {
    setComment(prev => ({ ...prev, replyCount: (prev.replyCount || 0) + 1 }));
    setIsReplying(false);
    setReplyInitialContent('');
    setReplies([]);
    setLastReplyVisible(null);
    setHasMoreReplies(true);
    if (!showReplies) {
        setShowReplies(true);
    }
    fetchReplies();
  };

  const handleCommentDeleted = (commentId) => {
    if (comment.id === commentId) {
      if (onCommentDeleted) {
        onCommentDeleted(commentId);
      }
    } else {
      setReplies(prevReplies => prevReplies.filter(reply => reply.id !== commentId));
      setComment(prev => ({ ...prev, replyCount: Math.max(0, (prev.replyCount || 1) - 1) }));
    }
  };

  const handleReply = (initialContent = '') => {
    setReplyInitialContent(initialContent);
    setIsReplying(!isReplying);
  };

  return (
    <div className="w-full">
      <CommentCard 
        comment={comment} 
        authorProfile={authorProfile} 
        entryId={entryId} 
        onReply={handleReply} 
        onCommentDeleted={handleCommentDeleted} 
        contentType={contentType} 
        nexusId={nexusId} 
        parentPath={currentPath} 
        nestingLevel={currentNestingLevel}
      />

      <div className={`reply-form-container ${isReplying ? 'open' : ''}`}>
        <div className="ml-14">
            <CommentForm
              entryId={entryId}
              parentId={currentNestingLevel === 2 ? topLevelParentId : comment.id}
              parentPath={currentPath} 
              onCommentAdded={handleReplySuccess}
              contentType={contentType}
              nexusId={nexusId}
              initialContent={replyInitialContent}
              nestingLevel={currentNestingLevel}
            />
        </div>
      </div>

      {comment.replyCount > 0 && currentNestingLevel < 2 && (
        <div className="ml-14 mt-2">
            <button onClick={handleToggleReplies} className="flex items-center text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                <LucideIcons.ChevronDown size={16} className={`mr-1 transition-transform ${showReplies ? 'rotate-180' : ''}`} />
                {showReplies ? t('commentThread.hideReplies') : t('commentThread.showReplies', { count: comment.replyCount })}
            </button>
        </div>
      )}

      {showReplies && (
        <div className="mt-4 space-y-4">
          {replies?.map(reply => (
            <div key={reply.id} className="comment-thread-container ml-8 pl-4">
                <div className="comment-thread-line"></div>
                <CommentThread 
                  comment={reply} 
                  entryId={entryId} 
                  userProfiles={userProfiles} 
                  contentType={contentType} 
                  nexusId={nexusId} 
                  onCommentDeleted={handleCommentDeleted} 
                  parentPath={currentPath} 
                  nestingLevel={currentNestingLevel + 1}
                  topLevelParentId={topLevelParentId || comment.id}
                />
            </div>
          ))}
          {isLoadingReplies && <p className="text-xs text-gray-400 animate-pulse ml-12">{t('commentThread.loadingReplies')}</p>}
          {hasMoreReplies && !isLoadingReplies && replies.length > 0 && (
            <div className="ml-12 mt-2">
                <button onClick={fetchReplies} className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors w-full text-left">{t('commentThread.loadMore')}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentThread;